import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import Card from 'components/Card';
import Typography from 'components/Typography/Typography';
import theme from 'themes';
import { DataScienceSurvey, MaintenanceEvent, MaintenanceEventStatus } from 'types/maintenance';
import { Table } from '.';
import { formatDate } from 'common/helpers/v1';
import Wrench from 'icons/indexed-icons/Wrench';
import { CustomCard } from '../MaintenanceEventDetailCard';

interface PredictiveCloseOutSurveyCard {
  event: MaintenanceEvent;
  survey?: DataScienceSurvey;
}

function getSurveyTitle(event: MaintenanceEvent, t: TFunction<'fpns'[], undefined>): string {
  if (event.status == MaintenanceEventStatus.NotComplete) {
    return t('mark_complete');
  } else if (event.status == MaintenanceEventStatus.Completed) {
    return t('ds_survey_completed');
  }
  return t('not_completed_as_planned');
}

function getSurveyHeaderColor(event: MaintenanceEvent) {
  if (event.status == MaintenanceEventStatus.Completed) {
    return theme.colors.white;
  } else if (event.status == MaintenanceEventStatus.NotComplete) {
    return theme.colors.white;
  }
  return theme.colors.white;
}

const PredictiveCloseOutSurveyCard = ({
  event,
  survey
}: PredictiveCloseOutSurveyCard): JSX.Element => {
  const { t } = useTranslation(['fpns']);
  const pmSurveyTitle = getSurveyTitle(event, t);
  const [headerColor] = useState<string>(getSurveyHeaderColor(event));
  return (
    <CustomCard>
      <Card.Header bgColor={headerColor} padding="0rem 1rem" icon={<Wrench />}>
        <Typography variant="tablerow" mb={0} size="0.8125rem" weight="medium">
          {pmSurveyTitle}
        </Typography>
      </Card.Header>
      <Card.Body pl={'3rem'} pt={0}>
        {event.status === MaintenanceEventStatus.Completed && event.completion && (
          <Table col={2.4} mb="1rem">
            <tbody>
              <tr>
                <td>
                  <Typography
                    as="span"
                    size="0.75rem"
                    weight="semi-bold"
                    color={theme.colors.lightGrey8}
                  >
                    Completed on:
                  </Typography>
                </td>
                <td>
                  <Typography
                    as="span"
                    size="0.75rem"
                    weight="semi-bold"
                    color={theme.colors.lightGrey8}
                  >
                    {event.completion
                      ? formatDate(event.completion, 'short')
                      : 'Scheduled Date Unavailable'}
                  </Typography>
                </td>
              </tr>
              {event.status == MaintenanceEventStatus.Completed && survey && (
                <>
                  <tr>
                    <td>
                      <Typography
                        as="span"
                        size="0.75rem"
                        weight="semi-bold"
                        color={theme.colors.lightGrey8}
                      >
                        Issue Type:
                      </Typography>
                    </td>
                    <td>
                      <Typography
                        as="span"
                        size="0.75rem"
                        weight="semi-bold"
                        color={theme.colors.lightGrey8}
                      >
                        {survey.issueType ? survey.issueType : 'Issue Type unavailable'}
                      </Typography>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Typography
                        as="span"
                        size="0.75rem"
                        weight="semi-bold"
                        color={theme.colors.lightGrey8}
                      >
                        Details:
                      </Typography>
                    </td>
                    <td>
                      <Typography
                        as="span"
                        size="0.75rem"
                        weight="semi-bold"
                        color={theme.colors.lightGrey8}
                      >
                        {survey.detail ? survey.detail : 'None'}
                      </Typography>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </CustomCard>
  );
};

export default PredictiveCloseOutSurveyCard;
